import { ListInterfacesOutput, RenameInterfaceInput } from "@zapier/bff";
import { trpc } from "../trpc";

type QueryInterfacesCache = ListInterfacesOutput | undefined;

const updateInterfaceInSearchResults = (
  cache: QueryInterfacesCache,
  { id, name }: RenameInterfaceInput,
): QueryInterfacesCache => {
  if (!cache) {
    return cache;
  }

  if (!cache.results) {
    return cache;
  }

  return {
    ...cache,
    results: cache.results.map((interfaceObject) =>
      interfaceObject.id === id
        ? { ...interfaceObject, name }
        : interfaceObject,
    ),
  };
};

interface Input {
  folderId?: string; // used for cache invalidation in mutations
}

export const useRenameInterface = (input?: Input) => {
  const { folderId } = input || {};
  const utils = trpc.useUtils();

  const mutation = trpc.interface.rename.useMutation({
    onMutate: (variables) => {
      utils.interface.list.cancel();

      utils.interface.list.setQueriesData(
        {},
        { exact: false },
        (cache: QueryInterfacesCache) =>
          updateInterfaceInSearchResults(cache, variables),
      );

      utils.interface.get.setData({ id: variables.id }, (cache) =>
        cache
          ? {
              ...cache,
              name: variables.name,
            }
          : cache,
      );

      utils.interface.getMeta.setData({ id: variables.id }, (cache) =>
        cache ? { ...cache, name: variables.name } : cache,
      );
    },
    onSettled: (_data, _error, variables) => {
      // Invalidate on settled so regardless of success or failure we refetch to show fresh data
      utils.interface.list.invalidate();

      if (variables?.id) {
        utils.interface.get.invalidate({ id: variables.id });
        utils.interface.getMeta.invalidate({ id: variables.id });
      }

      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({ folderId });
      }
    },
  });

  return {
    rename: mutation.mutate,
    isLoading: mutation.isPending,
  };
};
